import { downloadOwnersManual, downloadAllContent } from './offlineDownloader'

/**
 * In-memory offline download sessions keyed by vehicleId.
 * Survives React unmounts so navigation does not drop in-flight downloads,
 * duplicate starts, or completion handling for remounted UI.
 *
 * Stored on window so webpack HMR reloads do not orphan an in-flight download
 * from a fresh empty Map while IndexedDB writes continue.
 */

const GLOBAL_SESSIONS_KEY = '__twForestRiverOfflineDownloadSessions'
const GLOBAL_BEFORE_UNLOAD_KEY = '__twForestRiverOfflineDownloadBeforeUnload'

function getSessionsRoot() {
  if (typeof window !== 'undefined') return window
  return null
}

function getSessions() {
  const root = getSessionsRoot()
  if (!root) return new Map()
  if (!root[GLOBAL_SESSIONS_KEY]) {
    root[GLOBAL_SESSIONS_KEY] = new Map()
  }
  return root[GLOBAL_SESSIONS_KEY]
}

/**
 * @param {string|number} vehicleId
 * @returns {string|null}
 */
function normalizeVehicleId(vehicleId) {
  if (vehicleId == null || vehicleId === '') return null
  return String(vehicleId)
}

/**
 * @param {string} vehicleId
 * @returns {object} session record
 */
function ensureSession(vehicleId) {
  const sessions = getSessions()
  if (!sessions.has(vehicleId)) {
    sessions.set(vehicleId, {
      status: 'idle',
      downloadType: null,
      error: null,
      promise: null,
      listeners: new Set(),
    })
  }
  return sessions.get(vehicleId)
}

/**
 * @param {object} session
 * @param {object} event
 */
function emit(session, event) {
  session.listeners.forEach(listener => {
    try {
      listener(event)
    } catch (e) {
      console.error('[offline] Download session listener failed:', e)
    }
  })
}

/**
 * @param {object} session
 */
function emitStatus(session) {
  emit(session, {
    type: 'status',
    status: session.status,
    downloadType: session.downloadType,
    error: session.error,
  })
}

function handleBeforeUnload(event) {
  if (!hasActiveOfflineDownloads()) return
  event.preventDefault()
  event.returnValue = ''
}

function bindBeforeUnload() {
  if (typeof window === 'undefined') return
  if (window[GLOBAL_BEFORE_UNLOAD_KEY]) return
  window[GLOBAL_BEFORE_UNLOAD_KEY] = true
  window.addEventListener('beforeunload', handleBeforeUnload)
}

/**
 * @returns {boolean}
 */
export function hasActiveOfflineDownloads() {
  for (const session of getSessions().values()) {
    if (session.status === 'downloading') return true
  }
  return false
}

/**
 * Snapshot of the current download session for a vehicle.
 *
 * @param {string|number} vehicleId
 * @returns {object} session snapshot with status, downloadType, and error
 */
export function getOfflineDownloadSession(vehicleId) {
  const id = normalizeVehicleId(vehicleId)
  const sessions = getSessions()
  if (!id || !sessions.has(id)) {
    return { status: 'idle', downloadType: null, error: null }
  }
  const session = sessions.get(id)
  return {
    status: session.status,
    downloadType: session.downloadType,
    error: session.error,
  }
}

/**
 * Subscribe to download session events for a vehicle.
 * Immediately receives a status event with the current snapshot.
 *
 * @param {string|number} vehicleId
 * @param {Function} listener receives { type: 'status'|'complete'|'error', ... }
 * @returns {Function} unsubscribe
 */
export function subscribeOfflineDownloadSession(vehicleId, listener) {
  const id = normalizeVehicleId(vehicleId)
  if (!id || typeof listener !== 'function') {
    return () => {}
  }

  const session = ensureSession(id)
  session.listeners.add(listener)
  listener({
    type: 'status',
    status: session.status,
    downloadType: session.downloadType,
    error: session.error,
  })

  return () => {
    session.listeners.delete(listener)
  }
}

/**
 * Starts (or joins) an offline content download for a vehicle.
 * Concurrent calls for the same vehicle reuse the in-flight promise.
 *
 * @param {object} options
 * @param {string|number} options.vehicleId
 * @param {string} options.downloadType 'owners-manual' or 'all-content'
 * @param {object} options.contents
 * @param {object} options.ownersManualPdfs
 * @param {object} [options.vehicleInfo]
 * @param {Function} [options.onProgress]
 * @returns {Promise<object>} resolves with downloadType and result
 */
export function startOfflineDownload({
  vehicleId,
  downloadType,
  contents,
  ownersManualPdfs,
  vehicleInfo = null,
  onProgress,
}) {
  const id = normalizeVehicleId(vehicleId)
  if (!id) {
    return Promise.reject(new Error('vehicleId is required'))
  }
  if (!contents) {
    return Promise.reject(new Error('contents is required'))
  }
  if (downloadType !== 'owners-manual' && downloadType !== 'all-content') {
    return Promise.reject(new Error(`Unsupported download type: ${downloadType}`))
  }

  const session = ensureSession(id)

  if (session.status === 'downloading' && session.promise) {
    return session.promise
  }

  bindBeforeUnload()

  session.status = 'downloading'
  session.downloadType = downloadType
  session.error = null
  emitStatus(session)

  const downloadFn = downloadType === 'owners-manual'
    ? downloadOwnersManual
    : downloadAllContent

  session.promise = (async() => {
    try {
      const result = await downloadFn(
        id,
        contents,
        ownersManualPdfs,
        onProgress,
        vehicleInfo
      )

      session.status = 'idle'
      session.downloadType = null
      session.error = null
      emit(session, {
        type: 'complete',
        downloadType,
        result,
      })
      emitStatus(session)

      return { downloadType, result }
    } catch (e) {
      const message = 'Download failed. Please try again.'
      console.error('[offline]', message, e)
      session.status = 'error'
      session.downloadType = null
      session.error = message
      emit(session, {
        type: 'error',
        downloadType,
        error: message,
      })
      emitStatus(session)
      throw e
    } finally {
      session.promise = null
    }
  })()

  return session.promise
}

/**
 * Clears a transient error on the session (e.g. after the UI hides it).
 *
 * @param {string|number} vehicleId
 */
export function clearOfflineDownloadSessionError(vehicleId) {
  const id = normalizeVehicleId(vehicleId)
  const sessions = getSessions()
  if (!id || !sessions.has(id)) return

  const session = sessions.get(id)
  if (session.status !== 'error' && !session.error) return

  session.status = 'idle'
  session.error = null
  session.downloadType = null
  emitStatus(session)
}
